import { ChangeEvent, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ReactComponent as SearchIcon } from '../../../../../../assets/vectors/search.svg';
import { ReactComponent as ExcelIcon } from '../../../../../../assets/vectors/excel.svg';
import { useDIRProspects } from '../../../../../../hooks/fetchers/useDIRProspects/useDIRProspects';
import Pagination from '../../../../../commons/Pagination/Pagination';
import Loading from '../../../../../commons/Loading/Loading';
import { useMergedStatesFromFetchers } from '../../../../../../hooks/useMergedStatesFromFetchers/useMergedStatesFromFetchers';
import Empty from '../../../../../commons/Empty/Empty';
import { useMutation, useQueryClient } from 'react-query';
import { SearchProspectsFromDIRResponse } from '../../../../../../types/api/endpoints/prospect/prospect.types';
import {
  SEARCH_PROSPECT_FROM_DIR_QUERY_KEY,
  searchProspectFromDIRQuery,
} from './DashboardProspectsDIRTable.query';
import SearchInput from '../../../../../commons/SearchInput/SearchInput';
import Button from '../../../../../designSystem/Button/Button';
import { DashboardProspectsDIRContext } from '../DashboardProspectsDIR.context';
import { ButtonColor } from '../../../../../designSystem/Button/Button.types';

export default () => {
  const queryClient = useQueryClient();

  const { 
    filters, 
    setFiltersModalOpen, 
    setCreateModalOpen, 
    setExcelModalOpen,
    setAssignModalOpen, 
    setSelectedProspect 
  } = useContext(DashboardProspectsDIRContext);

  const [searchValue, setSearchValue] = useState<string>();
  const [searchResults, setSearchResults] = useState<SearchProspectsFromDIRResponse>();

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage] = useState<number>(25);

  const {
    data: prospects,
    refetch: refetchProspects,
    isLoading: isProspectsLoading,
    error,
  } = useDIRProspects({
    page: currentPage,
    perPage: itemsPerPage,
    commercialIds: filters.commercialIds,
  });

  const { mutate: searchMutate, isLoading: isSearchLoading } = useMutation<
    Awaited<SearchProspectsFromDIRResponse>,
    Error,
    string
  >(
    (data) =>
      searchProspectFromDIRQuery(data, {
        page: currentPage,
        perPage: itemsPerPage,
        commercialIds: filters.commercialIds,
      }),
    {
      mutationKey: SEARCH_PROSPECT_FROM_DIR_QUERY_KEY,
      onMutate: () => queryClient.cancelMutations(),
      onSuccess: (response) => {
        setSearchResults(response);
      },
      onError: (error) => {
        console.log('error', error);
      },
    }
  );

  useEffect(() => {
    if (searchResults) {
      searchMutate(searchValue || '');
    } else {
      queryClient.cancelMutations();
      refetchProspects();
    }
  }, [currentPage]);

  useEffect(() => {
    setCurrentPage(1);
    if (searchResults) {
      searchMutate(searchValue || '');
    } else {
      queryClient.cancelMutations();
      refetchProspects();
    }
  }, [filters]);

  const onChangeSearchText = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchValue(value);
      setCurrentPage(1);
      if (value.length === 0) {
        setSearchResults(undefined);
      } else {
        searchMutate(value);
      }
    },
    [searchMutate]
  );

  const numberOfPages = useMemo(() => {
    if (searchResults) {
      // @ts-ignore
      return parseInt(searchResults.total / itemsPerPage);
    } else {
      // @ts-ignore
      return parseInt(prospects?.total / itemsPerPage);
    }
  }, [prospects, searchResults, itemsPerPage]);

  const { isLoading } = useMergedStatesFromFetchers({
    loadings: [isProspectsLoading, isSearchLoading],
  });

  return (
    <>
      <div className="dashboard-commerciaux">
        <div className="dashboard-commerciaux__header">
          <SearchInput
            placeholder="Chercher un prospect..."
            startIcon={SearchIcon}
            onChange={onChangeSearchText}
          />
          <Button onClick={() => setFiltersModalOpen(true)} color={ButtonColor.Contrast}>Filtrer</Button>
          <Button onClick={() => setCreateModalOpen(true)}>Ajouter</Button>
          <Button onClick={() => setExcelModalOpen(true)} endIcon={ExcelIcon} color={ButtonColor.Excel}>
            Ajouter un fichier
          </Button>
        </div>
        <table className="dashboard__table dashboard-commerciaux__table" style={{ flex: 0 }}>
          {isLoading || error ? (
            error ? (
              <Empty>Aucun prospect trouvé</Empty>
            ) : (
              <Loading />
            )
          ) : (
            <>
              <thead>
                <tr>
                  <th>Commercial</th>
                  <th>Entreprise</th>
                  <th>Adresse email</th>
                  <th>N° de téléphone</th>
                  <th>Statut</th>
                </tr>
              </thead>
              <tbody>
                {(searchResults || prospects)?.results.map(
                  (prospect, index) => {
                    const { name_company, email, phone, status, firstNameCOM, lastNameCOM } = prospect
                    return (
                      <tr 
                        key={index} 
                        className='dashboard__table-tr--clickable'
                        onClick={() => {
                          setSelectedProspect(prospect)
                          setAssignModalOpen(true)
                        }}
                      >
                        <td>
                          {firstNameCOM} {lastNameCOM}
                        </td>
                        <td>{name_company}</td>
                        <td>{email}</td>
                        <td>{phone}</td>
                        <td className="text-primary">{status}</td>
                      </tr>
                    )
                  }
                )}
              </tbody>
            </>
          )}
        </table>
      </div>
      {!isLoading && numberOfPages > 0 && (
        <Pagination {...{ currentPage, setCurrentPage, numberOfPages }} />
      )}
    </>
  );
};
